import { create } from '@thoughtspot/logger';
import { getAnswerIdForAnswerEditSession } from '/@services/answer/answer-service';
import {
    AnswerEditSession,
    ChartViz,
    FalconDataType,
    HeadlineViz,
    LoadingState,
    PhraseType,
    QueryCompletionType,
    RecognizedTokenType,
    SageCompletion,
    SagePhrase,
    SageToken,
    Source,
    TableViz,
} from '/@services/generated/graphql-types';
import {
    ApolloClient,
    DocumentNode,
    gql,
} from '../../utils/error-handling/apollo-hooks-wrappers/custom-apollo-hooks';

const logger = create('search-service.util');

const bachSessionWithAcSessionKeyQuery: DocumentNode = gql`
    {
        sessionId
        genNo
        acSession {
            sessionId
            genNo
        }
    }
`;

/*
 * Returns an object for an empty token
 */
export function createNewEmptyToken(): SageToken {
    return {
        text: '',
        hasSpaceAfter: false,
        type: RecognizedTokenType.Unrecognized,
        dataType: FalconDataType.Unknown,
        isFromFormula: false,
        isFromCohort: false,
        recognizedTokenJson: null,
    };
}

/*
 * Returns an object for an empty phrase containing a single empty token
 */
export function createNewEmptyPhrase(): SagePhrase {
    return {
        type: PhraseType.UndefinedPhrase,
        tokens: [createNewEmptyToken()],
        isComplete: false,
        isAmbiguous: false,
    };
}

/*
 * Returns an object for a completion containing a single empty token
 */
export function createNewEmptyCompletion(): SageCompletion {
    return {
        completesCurrentPhrase: true,
        isDisabled: false,
        isHistory: false,
        isSynonym: false,
        nPrefixTokens: 0,
        nSuffixTokens: 0,
        startsNewPhrase: true,
        tokens: [createNewEmptyToken()],
        type: QueryCompletionType.Rewrite,
    };
}

export function getPhrasesOrDefaultEmptyPhrase(
    sagePhrases: SagePhrase[],
): SagePhrase[] {
    // If there are no phrases, return the default empty phrase state. This contains a single empty
    // token wrapped in a phrase, representing the "empty state" of the sage bar.
    if (!sagePhrases?.length) {
        return [createNewEmptyPhrase()];
    }

    return sagePhrases;
}

export function setSageBarLoading(
    client: ApolloClient<object>,
    answerSessionId: string,
    loadingState: LoadingState,
) {
    const sessionObject = {
        __typename: 'BachSessionId',
        sessionId: answerSessionId,
    };
    const session: any = client.cache.readQuery({
        id: client.cache.identify(sessionObject),
        query: bachSessionWithAcSessionKeyQuery,
    });

    const searchResponseObject = {
        id: session?.acSession,
        __typename: 'SageSearchResponse',
    };
    // A reopened SpotQL answer has no sage session (acSession === null), so
    // bachIdKeyFields returns false and identify() is undefined. There is no
    // sage bar to mark loading; writing with an undefined id throws Apollo
    // invariant #12 and aborts the calling Bach mutation.
    const searchResponseId = client.cache.identify(searchResponseObject);
    if (!searchResponseId) {
        logger.debug(
            'setSageBarLoading: no sage session for %s; skipping phrasesLoading write',
            answerSessionId,
        );
        return;
    }
    try {
        client.cache.writeFragment({
            id: searchResponseId,
            fragment: gql`
                fragment isLoading on SageSearchResponse {
                    phrasesLoading @client
                }
            `,
            data: {
                phrasesLoading: loadingState,
            },
        });
    } catch (error) {
        if (error?.message?.includes('already recomputing')) {
            // Suppress "already recomputing" error to prevent crashes
            return;
        }
        // Re-throw any other errors
        throw error;
    }
}

/*
 * After clearing the sage query, certain properties in the Answer object become stale. This
 * utility clears those properties from cache.
 * - selectedColumns: Checkmark state for columns in the data panel
 * - sources: Whether each source is accessible (grayed out) in the data panel
 * - filterGroups: Information about which filter values were selected
 */
export function clearStaleAnswerPropertiesInCache(
    client: ApolloClient<object>,
    answerSessionId: string,
) {
    try {
        client.cache.modify({
            id: client.cache.identify({
                id: getAnswerIdForAnswerEditSession(
                    client.cache,
                    answerSessionId,
                ),
                __typename: 'Answer',
            }),
            fields: {
                selectedColumns() {
                    return [];
                },
                sources(selectedSources: Source[]) {
                    return selectedSources.map(source => {
                        return {
                            ...source,
                            isAccessible: true,
                        };
                    });
                },
                filterGroups() {
                    return [];
                },
            },
        });
    } catch (error) {
        if (!error?.message?.includes('already recomputing')) {
            throw error;
        }
    }
}

export function clearAnswerVizInCache(
    client: ApolloClient<object>,
    answerSessionId: string,
) {
    const answerEditSession = {
        __typename: 'AnswerEditSession',
        id: {
            sessionId: answerSessionId,
        },
    };
    const answerSession: AnswerEditSession = client.cache.readQuery({
        id: client.cache.identify(answerEditSession),
        query: gql`
            {
                answer {
                    id
                    visualizations {
                        id
                    }
                }
            }
        `,
    });

    // Don't do anything if there is already no answer viz
    if (!answerSession.answer.visualizations?.length) {
        return;
    }

    // If we are removing visualization then cache.gc needs to be run else viz reference will be there
    // below cache.gc is not needed as we are removing viz by viz from the cache
    const visualizations = answerSession.answer.visualizations;
    visualizations.forEach((viz: ChartViz | TableViz | HeadlineViz) => {
        setTimeout(() => {
            client.cache.evict({
                id: client.cache.identify({
                    id: viz.id,
                    /* eslint-disable-next-line no-underscore-dangle */
                    __typename: viz.__typename,
                }),
            });
        }, 0);
    });
}

/*
 * Reads the sage phrases from the Bach Answer state, and writes them into the AC SageResponse
 * state. This can resolve issues arising from the two states being inconsistent, and the AC state
 * is stale or outdated.
 * TODO(Rifdhan) this is only a temporary workaround, we need a better permanent solution
 */
export function setSageSearchResponsePhrasesUsingAnswerPhrases(
    client: ApolloClient<object>,
    answerSessionId: string,
    acSessionId: string,
) {
    const answerSession: AnswerEditSession = client.cache.readQuery({
        id: client.cache.identify({
            id: {
                sessionId: answerSessionId,
            },
            __typename: 'AnswerEditSession',
        }),
        query: gql`
            {
                answer {
                    id
                    phrases {
                        tokens {
                            type
                            dataType
                            text
                            hasSpaceAfter
                            trailingWhitespace
                            isExtensible
                            isFromFormula
                            isFromCohort
                            column {
                                name
                                columnGuid
                            }
                            source {
                                id
                                name
                            }
                            recognizedTokenJson
                        }
                        type
                        isComplete
                        isAmbiguous
                    }
                }
            }
        `,
    });

    if (!answerSession?.answer?.phrases) {
        return;
    }

    try {
        client.cache.modify({
            id: client.cache.identify({
                id: {
                    sessionId: acSessionId,
                },
                __typename: 'SageSearchResponse',
            }),
            fields: {
                phrases() {
                    return answerSession.answer.phrases;
                },
            },
        });
    } catch (error) {
        if (!error?.message?.includes('already recomputing')) {
            throw error;
        }
    }
}
